import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { APPLICATION_NAME_TOKEN, ApplicationName } from '@ddt-web/config';
import { BrowserVersionUtil, LocalStorageKey } from '@ddt-web/generic-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { NotificationType } from '../models/notification-config';
import { Notification } from '../state/notifications.interface';
import { NotificationsService } from './notifications.service';

/**
 * BrowserNotificationService is used to display unsupported browser notification for IE
 * Used localStorage to make the notification dismissible across the apps
 */
@UntilDestroy()
@Injectable({
	providedIn: 'root'
})
export class BrowserNotificationService {

	private legacyBrowserKey: string = LocalStorageKey.ShowDeprecatedBrowserNotification + '_';

	constructor(
		private contentService: ContentService,
		private notificationsService: NotificationsService,
		@Inject(APPLICATION_NAME_TOKEN) private applicationName: ApplicationName
	) {

		if (this.applicationName) {
			this.legacyBrowserKey += this.applicationName;
		}

		this.notificationsService.getAllNotifications$().pipe(
			untilDestroyed(this)
		).subscribe((notifications: Notification[]) => {
			const dismissedNotification = notifications.filter(notification => notification.channel === 'browser' && notification.isDismissed === true);

			if (dismissedNotification?.length > 0) {
				localStorage.setItem(this.legacyBrowserKey, 'false');
			}
		});
	}

	public showNotification(): void {
		this.notificationsService.getAllVisibleNotifications$().pipe(
			untilDestroyed(this)
		).subscribe((notifications: Notification[]) => {
			const browserNotification = notifications.filter(notification => notification.channel === 'browser');
			const isLegacyBrowser = BrowserVersionUtil.isBrowserIE() || BrowserVersionUtil.isBrowserLegacyChrome();
			const shouldAddNotification = !browserNotification?.length && isLegacyBrowser && localStorage.getItem(this.legacyBrowserKey) !== 'false';

			if (shouldAddNotification) {
				const unsupportedBrowserMessage = this.contentService.get(
					'UNSUPPORTED_BROWSER_MESSAGE',
					'Some features will not work in Internet Explorer. Please use Chrome, Safari, Firefox or Edge instead.'
				);
				this.notificationsService.addNotification({ channel: 'browser', message: unsupportedBrowserMessage, type: NotificationType.ERROR });
				localStorage.setItem(this.legacyBrowserKey, 'true');
			}
		});
	}
}
